/**
 * New Mission — Manual creation form
 *
 * Replaces the raw chunked-upload page that was previously at /missions/upload.
 * Layout and UX deliberately mirrors SurveyMissionPlannerPage (Step 5) but is
 * manually filled rather than AI-generated.
 *
 * Data:
 *  GET  /api/approved-assets?asset_type=drone&status=active  → drone fleet
 *  GET  /api/satellite/boundaries                            → field list
 *  POST /api/missions                                        → create mission
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Navigation, Layers, AlertTriangle, CheckCircle2,
  Calendar, FileText, Loader2,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { missionsApi, satelliteBoundariesApi } from '../../services/api';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApprovedAsset {
  id: string;
  manufacturer: string;
  model: string;
  status: string;
  specifications?: { max_flight_time_min?: number };
}

interface Field {
  id: string;
  name: string;
  area_hectares?: number;
}

const VERTICALS = [
  { value: 'agrotech',        label: 'AgroTech — Estate Intelligence' },
  { value: 'precision',       label: 'AgroTech — Precision Planning' },
  { value: 'oil_gas',         label: 'Oil & Gas Intelligence' },
  { value: 'infrastructure',  label: 'Infrastructure Intelligence' },
  { value: 'environmental',   label: 'Environmental Geotech' },
  { value: 'core',            label: 'General / Other' },
];

const MISSION_TYPES = [
  { value: 'drone_survey',      label: 'Drone Survey (multispectral / RGB)' },
  { value: 'thermal_survey',    label: 'Thermal Survey' },
  { value: 'lidar_survey',      label: 'LiDAR Survey' },
  { value: 'remediation',       label: 'Remediation / Precision Spray' },
  { value: 'ground_inspection', label: 'Ground Inspection' },
  { value: 'other',             label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'planned',     label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed' },
];

const SELECT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

// ─── Component ────────────────────────────────────────────────────────────────

export default function LargeFileUploadPage() {
  const navigate = useNavigate();

  const [drones,  setDrones]  = useState<ApprovedAsset[]>([]);
  const [fields,  setFields]  = useState<Field[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);

  // Form state
  const [name,         setName]         = useState('');
  const [vertical,     setVertical]     = useState('');
  const [missionType,  setMissionType]  = useState('drone_survey');
  const [status,       setStatus]       = useState('planned');
  const [fieldId,      setFieldId]      = useState('');
  const [droneId,      setDroneId]      = useState('');
  const [flightDate,   setFlightDate]   = useState('');
  const [description,  setDescription]  = useState('');
  const [pilotNotes,   setPilotNotes]   = useState('');

  // ── Load drone fleet + fields ──────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const [droneRes, fieldRes] = await Promise.all([
        api.get('/approved-assets', { params: { asset_type: 'drone', status: 'active' } }),
        satelliteBoundariesApi.getAll(),
      ]);
      setDrones(droneRes.data?.data?.assets ?? []);
      setFields(fieldRes.data?.boundaries ?? fieldRes.boundaries ?? []);
    } catch {
      // Non-fatal — form still works without these lists
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Mission name is required'); return; }
    if (!vertical)    { setError('Please select a vertical'); return; }
    setSubmitting(true); setError(null);

    try {
      const drone = drones.find((d) => d.id === droneId);
      const field = fields.find((f) => f.id === fieldId);

      const res = await missionsApi.create({
        name:         name.trim(),
        description:  [
          description.trim(),
          pilotNotes ? `Pilot notes: ${pilotNotes.trim()}` : '',
        ].filter(Boolean).join('\n') || undefined,
        status,
        mission_type: missionType,
        ...(flightDate ? { start_time: new Date(flightDate).toISOString() } : {}),
        ...(drone ? { drone_serial_number: `${drone.manufacturer} ${drone.model}` } : {}),
        metadata: {
          vertical,
          field_id:    fieldId || undefined,
          field_name:  field?.name ?? undefined,
          area_hectares: field?.area_hectares ?? undefined,
          mission_source: 'manual',
        },
      });

      const missionId: string =
        res?.data?.mission?.id ?? res?.mission?.id ?? res?.id;
      const missionName: string =
        res?.data?.mission?.name ?? res?.mission?.name ?? name;

      setCreated({ id: missionId, name: missionName });
    } catch (e: any) {
      const d = e?.response?.data;
      setError(
        d?.error?.message ??
        (typeof d?.error === 'string' ? d.error : null) ??
        d?.message ?? e?.message ?? 'Failed to create mission'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success state ──────────────────────────────────────────────────────────
  if (created) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <div className="flex flex-col items-center text-center py-10 gap-5">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Mission Created</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                <span className="font-medium text-gray-700 dark:text-gray-300">{created.name}</span> has been saved as a planned mission.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate(`/missions/${created.id}`)}>
                Open Mission →
              </Button>
              <Button variant="outline" onClick={() => {
                setCreated(null);
                setName(''); setVertical(''); setMissionType('drone_survey'); setStatus('planned');
                setFieldId(''); setDroneId(''); setFlightDate('');
                setDescription(''); setPilotNotes('');
              }}>
                Create Another
              </Button>
              <Button variant="ghost" onClick={() => navigate('/missions')}>
                Back to Missions
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* ── Header ── */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => navigate('/missions')}
          className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors mt-0.5"
          aria-label="Back to missions"
        >
          <ArrowLeft className="w-5 h-5 text-gray-500" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
            <Navigation className="w-7 h-7 text-indigo-600 flex-shrink-0" /> New Mission
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manually plan a drone survey or inspection mission
          </p>
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Spinner size="lg" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* ── Mission Details ── */}
          <Card>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-500" /> Mission Details
            </h2>
            <div className="space-y-4">
              <Input
                label="Mission Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., North Block Survey — Block A"
                required
              />

              {/* Vertical — required */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vertical <span className="text-red-500">*</span>
                </label>
                <select
                  value={vertical}
                  onChange={(e) => setVertical(e.target.value)}
                  required
                  className={`${SELECT_CLS} ${!vertical ? 'text-gray-400' : ''}`}
                >
                  <option value="">— Select a vertical —</option>
                  {VERTICALS.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  The industry vertical this mission belongs to — used to group missions in Mission Control.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Mission Type
                  </label>
                  <select value={missionType} onChange={(e) => setMissionType(e.target.value)} className={SELECT_CLS}>
                    {MISSION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select value={status} onChange={(e) => setStatus(e.target.value)} className={SELECT_CLS}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" /> Planned Flight Date
                </label>
                <input
                  type="date"
                  value={flightDate}
                  onChange={(e) => setFlightDate(e.target.value)}
                  className={SELECT_CLS}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Objectives, area details, special instructions…"
                  className={`${SELECT_CLS} resize-none`}
                />
              </div>
            </div>
          </Card>

          {/* ── Field & Asset ── */}
          <Card>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-500" /> Field & Approved Asset
            </h2>
            <div className="space-y-4">

              {/* Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Field (optional)
                </label>
                {fields.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No fields registered — create fields in AgroTech → Fields first.</p>
                ) : (
                  <select value={fieldId} onChange={(e) => setFieldId(e.target.value)} className={SELECT_CLS}>
                    <option value="">— Select a field —</option>
                    {fields.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}{f.area_hectares ? ` · ${Number(f.area_hectares).toFixed(1)} ha` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Drone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Approved Drone (Approved Asset List)
                </label>
                {drones.length === 0 ? (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    No active drones — mission will be created without asset assignment.
                  </p>
                ) : (
                  <select value={droneId} onChange={(e) => setDroneId(e.target.value)} className={SELECT_CLS}>
                    <option value="">— Select a drone —</option>
                    {drones.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.manufacturer} {d.model}
                        {d.specifications?.max_flight_time_min
                          ? ` · ${d.specifications.max_flight_time_min} min endurance`
                          : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Pilot notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Pilot Notes (optional)
                </label>
                <textarea
                  value={pilotNotes}
                  onChange={(e) => setPilotNotes(e.target.value)}
                  rows={2}
                  placeholder="No-fly zones, access gates, waypoint adjustments…"
                  className={`${SELECT_CLS} resize-none`}
                />
              </div>
            </div>
          </Card>

          {/* ── Actions ── */}
          <div className="flex items-center gap-3">
            <Button type="submit" loading={submitting} disabled={submitting || !name.trim()} fullWidth>
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating Mission…</>
                : <><Navigation className="w-4 h-4 mr-2" />Create Mission</>}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/missions')}>
              Cancel
            </Button>
          </div>

        </form>
      )}
    </div>
  );
}

// Made with Bob
